if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.colorOptionHandles = ['color', 'colour', 'couleur', 'farbe', 'farg', 'farge', 'finish'];
        this.hardwareColorOptionHandles = ['hardware-color', 'hardware-colour', 'hardware'];
        this.genericMediaTerms = ['apple', 'watch', 'band', 'strap', 'case', 'color', 'colour', 'finish', 'titanium'];
        this.mql = window.matchMedia('(min-width: 750px)');
        this.initializeGallery();
      }

      connectedCallback() {
        this.initializeGallery();
      }

      initializeGallery() {
        if (this.initialized) return;

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        if (!this.elements.viewer) return;
        this.initialized = true;
        this.elements.viewer.enableSliderLooping = true;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.onOptionValueChangeUnsubscriber = subscribe(
            PUB_SUB_EVENTS.optionValueSelectionChange,
            this.onOptionValueChanging.bind(this)
          );
        }
        this.elements.viewer
          .querySelectorAll('button[name="previous"], button[name="next"]')
          .forEach((button) => button.addEventListener('click', this.onGalleryButtonClick.bind(this), true));

        this.elements.thumbnails?.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            ?.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });

        this._initSwipe();

        this.setActiveMedia(this.getActiveMediaItem()?.dataset.mediaId, false);
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      disconnectedCallback() {
        this.onOptionValueChangeUnsubscriber?.();
        this.clearPendingMediaFallback();
      }

      onOptionValueChanging({ data: { event } }) {
        const productInfo = this.closest('product-info');
        if (!productInfo || !productInfo.contains(event.target)) return;

        this.showPendingMediaFallback(this.getActiveMediaItem());
      }

      onSlideChanged(event) {
        const mediaId = event.detail.currentElement?.dataset?.mediaId;
        if (!mediaId || this.isFilteredMediaId(mediaId)) return;

        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        if (!activeMedia) return;

        activeMedia.classList.add('is-active');
        this.getMediaItems().forEach((element) => {
          if (element !== activeMedia) element.classList.remove('is-active');
        });

        this.updateGalleryButtons(activeMedia);

        const thumbnail = this.elements.thumbnails?.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(thumbnail);
        this.updateGalleryCounter(activeMedia);
      }

      onGalleryButtonClick(event) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.navigateGallery(event.currentTarget.name);
      }

      navigateGallery(direction, immediate = false) {
        const visibleMediaItems = this.getVisibleMediaItems();
        if (visibleMediaItems.length < 2) return;

        const activeIndex = Math.max(visibleMediaItems.indexOf(this.getActiveMediaItem()), 0);
        const nextIndex =
          direction === 'next'
            ? (activeIndex + 1) % visibleMediaItems.length
            : (activeIndex - 1 + visibleMediaItems.length) % visibleMediaItems.length;
        const nextMedia = visibleMediaItems[nextIndex];

        if (!nextMedia) return;

        if (immediate) {
          this._activateMedia(nextMedia);
          return;
        }

        this.setActiveMedia(nextMedia.dataset.mediaId, false);
      }

      _initSwipe() {
        const swipeState = {
          startX: 0,
          startY: 0,
          touchId: null,
          pointerId: null,
          horizontal: null,
          maxDx: 0,
          maxDy: 0,
        };

        const resetSwipeState = () => {
          swipeState.startX = 0;
          swipeState.startY = 0;
          swipeState.touchId = null;
          swipeState.pointerId = null;
          swipeState.horizontal = null;
          swipeState.maxDx = 0;
          swipeState.maxDy = 0;
        };

        const getTrackedTouch = (touchList) =>
          Array.from(touchList || []).find((touch) => touch.identifier === swipeState.touchId);

        const beginSwipe = (clientX, clientY, id) => {
          swipeState.startX = clientX;
          swipeState.startY = clientY;
          swipeState.touchId = null;
          swipeState.pointerId = id;
          swipeState.horizontal = null;
          swipeState.maxDx = 0;
          swipeState.maxDy = 0;
        };

        const updateSwipeDirection = (clientX, clientY, event) => {
          if (swipeState.pointerId === null && swipeState.touchId === null) return false;

          const dx = clientX - swipeState.startX;
          const dy = clientY - swipeState.startY;
          swipeState.maxDx = Math.max(swipeState.maxDx, Math.abs(dx));
          swipeState.maxDy = Math.max(swipeState.maxDy, Math.abs(dy));

          if (swipeState.horizontal !== null) {
            if (swipeState.horizontal) event.preventDefault();
            return swipeState.horizontal;
          }

          const absDx = Math.abs(dx);
          const absDy = Math.abs(dy);

          // Only abort to vertical when it is clearly dominant — prevents diagonal
          // gesture starts from misclassifying an intended horizontal swipe.
          if (absDy >= 8 && absDy > absDx * 2.0) {
            swipeState.horizontal = false;
            return false;
          }

          // Commit to horizontal once it is unambiguously leading.
          if (absDx >= 4 && absDx > absDy) {
            swipeState.horizontal = true;
            event.preventDefault();
            return true;
          }

          return false;
        };

        const finishSwipe = (clientX, clientY) => {
          const dx = clientX - swipeState.startX;
          const dy = clientY - swipeState.startY;
          const travel = Math.max(Math.abs(dx), swipeState.maxDx);
          const verticalTravel = Math.max(Math.abs(dy), swipeState.maxDy);
          const shouldNavigate =
            swipeState.horizontal === true && travel > 4 && travel > verticalTravel;
          resetSwipeState();

          if (!shouldNavigate) return;

          this.navigateGallery(dx < 0 ? 'next' : 'previous', true);
        };

        this.elements.viewer.style.touchAction = 'none';
        if (this.elements.viewer.slider) this.elements.viewer.slider.style.touchAction = 'none';

        if (window.PointerEvent) {
          this.elements.viewer.addEventListener(
            'pointerdown',
            (event) => {
              if (event.pointerType !== 'touch') return;
              beginSwipe(event.clientX, event.clientY, event.pointerId);
              event.currentTarget.setPointerCapture?.(event.pointerId);
            },
            { passive: true }
          );

          this.elements.viewer.addEventListener(
            'pointermove',
            (event) => {
              if (swipeState.pointerId !== event.pointerId) return;
              updateSwipeDirection(event.clientX, event.clientY, event);
            },
            { passive: false }
          );

          this.elements.viewer.addEventListener(
            'pointerup',
            (event) => {
              if (swipeState.pointerId !== event.pointerId) return;
              finishSwipe(event.clientX, event.clientY);
            },
            { passive: true }
          );

          this.elements.viewer.addEventListener(
            'pointercancel',
            (event) => {
              if (swipeState.pointerId !== event.pointerId) return;
              resetSwipeState();
            },
            { passive: true }
          );

          return;
        }

        this.elements.viewer.addEventListener(
          'touchstart',
          (event) => {
            if (event.touches.length !== 1) {
              resetSwipeState();
              return;
            }

            const touch = event.touches[0];
            beginSwipe(touch.clientX, touch.clientY, touch.identifier);
            swipeState.touchId = touch.identifier;
          },
          { passive: true }
        );

        this.elements.viewer.addEventListener(
          'touchmove',
          (event) => {
            if (swipeState.touchId === null) return;

            const touch = getTrackedTouch(event.touches);
            if (!touch) return;

            updateSwipeDirection(touch.clientX, touch.clientY, event);
          },
          { passive: false }
        );

        this.elements.viewer.addEventListener(
          'touchend',
          (event) => {
            if (swipeState.touchId === null) return;

            const touch = getTrackedTouch(event.changedTouches) || event.changedTouches[0];
            if (!touch) {
              resetSwipeState();
              return;
            }

            finishSwipe(touch.clientX, touch.clientY);
          },
          { passive: true }
        );

        this.elements.viewer.addEventListener(
          'touchcancel',
          () => {
            resetSwipeState();
          },
          { passive: true }
        );
      }

      setActiveMedia(mediaId) {
        // Cancel any pending off-screen preload from a previous call
        this._pendingMediaLoad?.cancel();

        const activeItemsBeforeFilter = this.getMediaItems().filter((mediaItem) =>
          mediaItem.classList.contains('is-active')
        );
        const stableActiveMedia =
          activeItemsBeforeFilter.find((mediaItem) => mediaItem.dataset.mediaId !== mediaId) ||
          activeItemsBeforeFilter[0] ||
          null;

        this.applyVariantMediaFilter(mediaId, stableActiveMedia?.dataset.mediaId);

        const visibleMediaItems = this.getVisibleMediaItems();
        if (!visibleMediaItems.length) return;

        const activeMedia =
          visibleMediaItems.find((mediaItem) => mediaItem.dataset.mediaId === mediaId) ||
          visibleMediaItems.find((mediaItem) => mediaItem.classList.contains('is-active')) ||
          visibleMediaItems[0];

        const img = activeMedia.querySelector('.product__media img');
        const hasStableActiveMedia = stableActiveMedia && stableActiveMedia !== activeMedia;
        const alreadyActive = activeMedia.classList.contains('is-active') && !hasStableActiveMedia;

        if (img && !img.complete) {
          if (alreadyActive) {
            // DOM-swap path: Dawn's viewTransition inserts the new section into the DOM
            // (triggering this constructor) BEFORE hiding the old section. The old
            // media-gallery is still live — read its active image's currentSrc (the exact
            // browser-selected srcset URL) and pin it as a background so the user sees the
            // old image instead of a blank while the new image loads.
            const mediaEl = activeMedia.querySelector('.product__media');
            const oldGallery = Array.from(document.querySelectorAll('media-gallery')).find((g) => g !== this);
            const oldImg = oldGallery?.querySelector('[data-media-id].is-active .product__media img');
            const oldSrc = oldImg?.currentSrc || oldImg?.src || null;
            this.showPendingMediaFallback(null, oldSrc);
            if (mediaEl && oldSrc) {
              mediaEl.style.cssText = `background-image:url("${oldSrc}");background-size:contain;background-repeat:no-repeat;background-position:center`;
            }
            const spinner = activeMedia.querySelector('.loading__spinner');
            spinner?.classList.remove('hidden');
            const cleanup = () => {
              this.clearPendingMediaFallback();
              if (mediaEl) mediaEl.style.cssText = '';
              spinner?.classList.add('hidden');
            };
            img.addEventListener('load', cleanup, { once: true });
            img.addEventListener('error', cleanup, { once: true });
            // Fall through to _activateMedia so counter/buttons update immediately.
          } else {
            // Gallery-switch path: slide is not yet visible. Keep old slide showing by
            // preloading off-screen so the browser picks the correct srcset URL.
            let cancelled = false;
            let activated = false;
            const originalLoading = img.getAttribute('loading');
            this.showPendingMediaFallback(stableActiveMedia);
            activeMedia.classList.remove('is-active');
            activeMedia.style.cssText = 'display:block;visibility:hidden;position:absolute;z-index:-1;pointer-events:none';
            img.setAttribute('loading', 'eager');

            const activate = () => {
              if (activated) return;
              activated = true;
              if (cancelled) {
                this.clearPendingMediaFallback();
                activeMedia.style.cssText = '';
                if (originalLoading === null) {
                  img.removeAttribute('loading');
                } else {
                  img.setAttribute('loading', originalLoading);
                }
                return;
              }
              activeMedia.style.cssText = '';
              this._pendingMediaLoad = null;
              if (originalLoading === null) {
                img.removeAttribute('loading');
              } else {
                img.setAttribute('loading', originalLoading);
              }
              this.clearPendingMediaFallback();
              this._activateMedia(activeMedia);
            };

            img.addEventListener('load', activate, { once: true });
            img.addEventListener('error', activate, { once: true });
            img.decode?.().then(activate).catch(() => {});
            const timeout = setTimeout(activate, 3000);

            this._pendingMediaLoad = {
              cancel: () => {
                cancelled = true;
                clearTimeout(timeout);
                this.clearPendingMediaFallback();
                activeMedia.style.cssText = '';
                if (originalLoading === null) {
                  img.removeAttribute('loading');
                } else {
                  img.setAttribute('loading', originalLoading);
                }
                img.removeEventListener('load', activate);
                img.removeEventListener('error', activate);
              },
            };
            return;
          }
        }

        this._activateMedia(activeMedia);
      }

      _activateMedia(activeMedia) {
        // Add is-active to new item before removing from old — prevents the one-frame gap
        // where :not(.is-active) { display:none } would blank the gallery between the two ops.
        activeMedia.classList.add('is-active');
        this.getMediaItems().forEach((element) => {
          if (element !== activeMedia) element.classList.remove('is-active');
        });
        this.applyVariantMediaFilter(activeMedia.dataset.mediaId);
        this.updateGalleryCounter(activeMedia);
        this.updateGalleryButtons(activeMedia);

        this.preventStickyHeader();
        window.setTimeout(() => {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft, behavior: 'instant' });

          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });

        this.playActiveMedia(activeMedia);

        const activeThumbnail = this.elements.thumbnails?.querySelector(`[data-target="${activeMedia.dataset.mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, this.getVisibleMediaItems().indexOf(activeMedia) + 1);
      }

      applyVariantMediaFilter(preserveMediaId = null, transitionMediaId = null) {
        const mediaItems = this.getMediaItems();
        const matchingMediaItems = this.getMatchingMediaItems(mediaItems);
        const visibleMediaIds = new Set(matchingMediaItems.map((mediaItem) => mediaItem.dataset.mediaId));

        // Variant featured media always takes precedence over the alt-text filter
        if (preserveMediaId) visibleMediaIds.add(preserveMediaId);

        // Keep the displayed item in the visible set during the transition
        // so it is never set to hidden=true while it still holds is-active.
        // Newly fetched variant media can arrive with is-active from the section
        // HTML, so the caller passes the prior displayed media explicitly.
        if (transitionMediaId) visibleMediaIds.add(transitionMediaId);

        mediaItems.forEach((mediaItem) => {
          const isFiltered = !visibleMediaIds.has(mediaItem.dataset.mediaId);
          mediaItem.hidden = isFiltered;
          mediaItem.classList.toggle('is-filtered', isFiltered);
        });

        this.elements.thumbnails?.querySelectorAll('[data-target]').forEach((thumbnail) => {
          const isFiltered = !visibleMediaIds.has(thumbnail.dataset.target);
          thumbnail.hidden = isFiltered;
          thumbnail.classList.toggle('is-filtered', isFiltered);
        });

        this.elements.viewer.resetPages?.();
        this.elements.thumbnails?.resetPages?.();
      }

      getMatchingMediaItems(mediaItems) {
        const selectedTerms = this.getSelectedMediaTerms();
        if (!Object.keys(selectedTerms).length) return mediaItems;

        const hasSpecificTerms = selectedTerms.color || selectedTerms.hardware;
        const componentMatches = hasSpecificTerms
          ? mediaItems.filter((mediaItem) => {
              const mediaColor = this.normalizeText(mediaItem.dataset.mediaColor);
              const mediaHardware = this.normalizeText(mediaItem.dataset.mediaHardware);

              const colorMatch = !selectedTerms.color || mediaColor === selectedTerms.color;
              const hardwareMatch = !selectedTerms.hardware || mediaHardware === selectedTerms.hardware;

              // If it matches both selected terms exactly, it's a winner
              if (colorMatch && hardwareMatch) return true;

              // Fallback: if media only has one term but it matches one of our specific selections
              // (e.g. generic product with only Color option)
              if (!mediaHardware && colorMatch && !selectedTerms.hardware) return true;

              return false;
            })
          : [];

        if (componentMatches.length) return componentMatches;

        const fullTitleMatches = selectedTerms.fullTitle
          ? mediaItems.filter((mediaItem) => this.normalizeText(mediaItem.dataset.mediaAlt) === selectedTerms.fullTitle)
          : [];

        return fullTitleMatches.length ? fullTitleMatches : mediaItems;
      }

      getSelectedMediaTerms() {
        const fullTitle = this.getSelectedVariantTitle();
        const productInfo = this.closest('product-info');

        const getVal = (handles) => {
          const group = handles
            .map((h) => productInfo?.querySelector(`.product-form__input[data-option-handle="${h}"]`))
            .find(Boolean);
          if (!group) return null;
          const val = group.querySelector('input:checked')?.value ||
                      group.querySelector('select')?.value ||
                      group.querySelector('[data-selected-value]')?.textContent;
          return this.normalizeText(val);
        };

        const color = getVal(this.colorOptionHandles);
        const hardware = getVal(this.hardwareColorOptionHandles);

        const terms = {};
        if (color && !this.genericMediaTerms.includes(color)) terms.color = color;
        if (hardware && !this.genericMediaTerms.includes(hardware)) terms.hardware = hardware;
        if (fullTitle) terms.fullTitle = fullTitle;

        return terms;
      }

      getSelectedVariantTitle() {
        const selectedVariant = this.closest('product-info')?.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        if (!selectedVariant) return '';

        try {
          const parsedVariant = JSON.parse(selectedVariant);
          const variantTitle = this.normalizeText(parsedVariant?.public_title || parsedVariant?.title);
          return variantTitle && variantTitle !== 'default title' ? variantTitle : '';
        } catch (error) {
          console.error('Unable to parse selected variant JSON', error);
          return '';
        }
      }

      getMediaItems() {
        return Array.from(this.elements.viewer.querySelectorAll('.product__media-list > [data-media-id]'));
      }

      getVisibleMediaItems() {
        return this.getMediaItems().filter((mediaItem) => !mediaItem.hidden && !mediaItem.classList.contains('is-filtered'));
      }

      getActiveMediaItem() {
        return this.elements.viewer.querySelector('.product__media-list > [data-media-id].is-active');
      }

      isFilteredMediaId(mediaId) {
        const mediaItem = this.elements.viewer.querySelector(`.product__media-list > [data-media-id="${mediaId}"]`);
        return !mediaItem || mediaItem.hidden || mediaItem.classList.contains('is-filtered');
      }

      normalizeText(value) {
        return String(value || '')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, ' ')
          .trim()
          .replace(/\s+/g, ' ');
      }

      updateGalleryCounter(activeMedia) {
        const visibleMediaItems = this.getVisibleMediaItems();
        const current = this.elements.viewer.querySelector('.slider-counter--current');
        const total = this.elements.viewer.querySelector('.slider-counter--total');
        if (!current || !total || !activeMedia) return;

        const currentValue = String(visibleMediaItems.indexOf(activeMedia) + 1);
        const totalValue = String(visibleMediaItems.length);
        if (current.textContent !== currentValue) current.textContent = currentValue;
        if (total.textContent !== totalValue) total.textContent = totalValue;
      }

      updateGalleryButtons(activeMedia) {
        const previousButton = this.elements.viewer.querySelector('button[name="previous"]');
        const nextButton = this.elements.viewer.querySelector('button[name="next"]');

        previousButton?.removeAttribute('disabled');
        nextButton?.removeAttribute('disabled');
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button')?.setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image || !this.elements.liveRegion) return;

        const announceImage = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };

        if (image.complete) {
          announceImage();
        } else {
          image.addEventListener('load', announceImage, { once: true });
        }
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      showPendingMediaFallback(stableActiveMedia, fallbackSrc = null) {
        const img = stableActiveMedia?.querySelector('.product__media img');
        const src = fallbackSrc || img?.currentSrc || img?.src;
        if (!src || !this.elements.viewer) return () => {};

        this.clearPendingMediaFallback();
        this.elements.viewer.style.setProperty('--variant-media-pending-image', `url("${src}")`);
        this.classList.add('media-gallery--variant-pending');

        const timeout = setTimeout(() => this.clearPendingMediaFallback(), 3000);
        this._pendingMediaFallbackCleanup = () => {
          clearTimeout(timeout);
          this.classList.remove('media-gallery--variant-pending');
          this.elements.viewer.style.removeProperty('--variant-media-pending-image');
        };
        return this._pendingMediaFallbackCleanup;
      }

      clearPendingMediaFallback() {
        this._pendingMediaFallbackCleanup?.();
        this._pendingMediaFallbackCleanup = null;
      }

      clearPendingMediaFallbackWhenVisible(activeMedia, startedAt = performance.now(), visibleSince = null) {
        if (!this._pendingMediaFallbackCleanup) return;

        const now = performance.now();
        const styles = window.getComputedStyle(activeMedia);
        const galleryStyles = window.getComputedStyle(this);
        const rect = activeMedia.getBoundingClientRect();
        const isVisible =
          galleryStyles.display !== 'none' &&
          galleryStyles.visibility !== 'hidden' &&
          styles.display !== 'none' &&
          styles.visibility !== 'hidden' &&
          rect.width > 1 &&
          rect.height > 1;

        const nextVisibleSince = isVisible ? visibleSince || now : null;

        if ((nextVisibleSince && now - nextVisibleSince > 700) || now - startedAt > 3000) {
          this.clearPendingMediaFallback();
          return;
        }

        window.requestAnimationFrame(() =>
          this.clearPendingMediaFallbackWhenVisible(activeMedia, startedAt, nextVisibleSince)
        );
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
